import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import ClockRewindIcon from "@redotech/redo-icons/clock-rewind_filled.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  getFlowVersions,
  revertFlow,
} from "@redotech/redo-merchant-app-common/client/team";
import { FlowType } from "@redotech/redo-model/return-flow";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type {
  RedoListItem,
  RedoListItemSelectedSource,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { formatDateTime } from "@redotech/redo-web/time";
import { memo, useCallback, useMemo, useState } from "react";

interface FlowVersion {
  _id: string;
  name: string;
  publishedAt: string;
}

interface FlowVersionHistoryProps {
  flowType: FlowType;
  onReverted(flow: unknown): void;
}

export const FlowVersionHistory = memo(function FlowVersionHistory({
  flowType,
  onReverted,
}: FlowVersionHistoryProps) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [confirmVersion, setConfirmVersion] = useState<FlowVersion | null>(
    null,
  );

  const versionsLoad = useLoad(
    async (signal) => {
      const { flows } = await getFlowVersions(client, flowType, { signal });
      return (flows as unknown as FlowVersion[]).sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
    },
    [client, flowType],
  );

  const [revertLoad, doRevert] = useTriggerLoad(async (signal) => {
    if (!confirmVersion) {
      return;
    }
    const { flow } = await revertFlow(client, flowType, confirmVersion._id, {
      signal,
    });
    onReverted(flow);
    setConfirmVersion(null);
  });

  const items: RedoListItem<FlowVersion>[] = useMemo(() => {
    const versions = versionsLoad.value;
    if (!versions || versions.length <= 1) {
      return [
        {
          id: "empty",
          value: {} as FlowVersion,
          type: "text" as const,
          text: "No previous versions",
        },
      ];
    }
    return versions.map((v, i) => ({
      id: v._id,
      value: v,
      type: "text" as const,
      text: v.name || "Unnamed version",
      supportingText: formatDateTime(
        Temporal.Instant.fromEpochMilliseconds(
          new Date(v.publishedAt).getTime(),
        ),
      ),
      ...(i === 0 && {
        infoBadge: { text: "Active", color: "success" as const },
      }),
    }));
  }, [versionsLoad.value]);

  const handleItemSelected = useCallback(
    ({
      item,
    }: {
      item: RedoListItem<FlowVersion>;
      source: RedoListItemSelectedSource;
      index: number;
      selected: boolean;
    }) => {
      if (item.id === "empty") {
        return;
      }
      setDropdownOpen(false);
      setConfirmVersion(item.value);
    },
    [],
  );

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconLeading={ClockRewindIcon}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setDropdownOpen(!dropdownOpen);
        }}
        size="sm"
        text="Version history"
      />
      <RedoListDropdown
        dropdownAnchor={anchorEl}
        dropdownOpen={dropdownOpen}
        dropdownPlacement="bottom-end"
        items={items}
        itemSelected={handleItemSelected}
        setDropdownOpen={setDropdownOpen}
        size="sm"
      />
      {confirmVersion && (
        <RedoModal
          isOpen
          modalSize="sm"
          onModalCloseRequested={() => setConfirmVersion(null)}
          primaryButton={{
            text: "Restore",
            size: "sm",
            onClick: () => doRevert(),
            pending: revertLoad.pending,
          }}
          secondaryButton={{
            text: "Cancel",
            size: "sm",
            onClick: () => setConfirmVersion(null),
          }}
          subtitle={`"${confirmVersion.name || "Unnamed version"}" (last published ${formatDateTime(Temporal.Instant.fromEpochMilliseconds(new Date(confirmVersion.publishedAt).getTime()))}) will become the active flow. Your current flow stays in version history.`}
          title="Restore this version?"
        />
      )}
    </>
  );
});
